import React from "react";
import { amountInWords, companyLogoUrl, companySignatureUrl, inr } from "@/lib/erp";
import { resolveDocumentBank, resolveDocumentText } from "@/lib/documentSettings";

export default function DocumentPrint({ doc, company, title, type, printArea = false }) {
  const c = company || doc.company || {};
  const bank = resolveDocumentBank(c, doc);
  const text = resolveDocumentText(c, doc, type);
  const items = doc.items || [];
  const customKeys = Array.from(new Set(items.flatMap((item) => Object.keys(item.custom || {}))))
    .filter((key) => items.some((item) => (item.custom || {})[key]));
  const purchase = type.startsWith("purchase_");
  const partyName = purchase ? doc.vendor_name : doc.customer_name;
  const partyAddress = purchase ? (doc.vendor_address || doc.billing_address) : doc.billing_address;
  const partyGstin = purchase ? doc.vendor_gstin : doc.customer_gstin;
  const number = doc.doc_no || doc.cn_no || doc.invoice_no;
  const date = doc.doc_date || doc.bill_date || doc.cn_date;
  const logo = c.print_show_logo !== false ? companyLogoUrl(c) : null;
  const showSignature = c.print_show_signature !== false;
  const signature = showSignature ? companySignatureUrl(c) : null;

  return (
    <div id={printArea ? "print-area" : undefined} data-print-format="a4" style={{ width: "210mm", margin: "0 auto" }}
      className="bg-white text-slate-900 text-[12px] leading-snug p-6 font-sans box-border">
      <div className="flex justify-between items-start border-b-2 border-slate-800 pb-3">
        <div>
          {logo && <img src={logo} alt="" style={{ maxHeight: "56px", maxWidth: "220px", objectFit: "contain", marginBottom: "6px" }} />}
          <div className="font-heading text-xl font-bold">{c.name}</div>
          <div className="text-slate-600 mt-1 max-w-xs">{c.address}</div>
          {c.print_show_gstin !== false && c.gstin && <div className="mt-1">GSTIN: <span className="font-mono font-semibold">{c.gstin}</span></div>}
          {c.dl_no && <div className="text-slate-600">DL: {c.dl_no}</div>}
          {(c.phone || c.email) && <div className="text-slate-600">{[c.phone, c.email].filter(Boolean).join(" · ")}</div>}
        </div>
        <div className="text-right">
          <div className="inline-block border border-slate-800 px-3 py-1 font-heading font-bold text-base uppercase">{title}</div>
          <div className="mt-2 font-mono text-sm font-semibold">{number}</div>
          {date && <div className="text-[11px] text-slate-500 mt-1">Date: {date}</div>}
          {doc.valid_until && <div className="text-[11px] text-slate-500">Valid until: {doc.valid_until}</div>}
          {doc.bill_no && <div className="text-[11px] text-slate-500">Bill: {doc.bill_no}</div>}
          {doc.invoice_no && type === "credit_note" && <div className="text-[11px] text-slate-500">Against invoice: {doc.invoice_no}</div>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 py-3 border-b border-slate-300">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{purchase ? "Vendor" : "Bill To"}</div>
          <div className="font-semibold">{partyName}</div>
          <div className="text-slate-600 whitespace-pre-wrap">{partyAddress}</div>
          {partyGstin && <div>GSTIN: <span className="font-mono">{partyGstin}</span></div>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{purchase ? "Document Details" : "Ship To"}</div>
          {!purchase && <div className="text-slate-600 whitespace-pre-wrap">{doc.shipping_address || doc.billing_address}</div>}
          {doc.transporter && <div className="mt-1">Transporter: {doc.transporter}{doc.vehicle_no ? ` · ${doc.vehicle_no}` : ""}</div>}
          {doc.reason && <div className="mt-1">Reason: {doc.reason}{doc.mode ? ` · ${doc.mode}` : ""}</div>}
        </div>
      </div>

      <table className="w-full mt-3 border-collapse text-[11px]">
        <thead><tr className="bg-slate-100 text-left">
          <Th>#</Th><Th>Product</Th><Th>HSN</Th><Th>Batch</Th>
          {customKeys.map((key) => <Th key={key}>{key}</Th>)}
          <Th right>Qty</Th><Th right>Rate</Th><Th right>GST%</Th><Th right>Amount</Th>
        </tr></thead>
        <tbody>{items.map((item, i) => (
          <tr key={i}>
            <Td>{i + 1}</Td><Td>{item.name}</Td><Td mono>{item.hsn}</Td><Td mono>{item.batch_no}</Td>
            {customKeys.map((key) => <Td key={key}>{(item.custom || {})[key] || ""}</Td>)}
            <Td right>{item.qty}</Td><Td right mono>{Number(item.rate || 0).toFixed(2)}</Td>
            <Td right>{item.gst_rate ?? 0}</Td><Td right mono>{Number(item.amount || 0).toFixed(2)}</Td>
          </tr>
        ))}</tbody>
      </table>

      <div className="flex justify-between gap-4 mt-3 print-keep">
        <div className="text-[11px] max-w-[55%]">
          <div className="font-semibold">Amount in Words:</div>
          <div className="italic">{amountInWords(doc.grand_total)}</div>
          {c.print_show_bank !== false && bank && <div className="mt-3 border border-slate-300 rounded p-2">
            <div className="font-semibold mb-0.5">Bank Details</div><BankFields bank={bank} />
          </div>}
        </div>
        <div className="w-[40%] text-[11px]">
          <Row label="Taxable Amount" value={doc.subtotal} />
          {doc.intra_state ? <><Row label="CGST" value={doc.cgst} /><Row label="SGST" value={doc.sgst} /></> : <Row label="IGST" value={doc.igst} />}
          {doc.total_discount > 0 && <Row label="Discount" value={doc.total_discount} />}
          {doc.shipping > 0 && <Row label="Shipping" value={doc.shipping} />}
          {doc.other_charges > 0 && <Row label="Other Charges" value={doc.other_charges} />}
          <Row label="Round Off" value={doc.round_off} />
          <div className="flex justify-between border-t-2 border-slate-800 pt-1 mt-1 font-bold text-sm"><span>Grand Total</span><span className="font-mono">{inr(doc.grand_total)}</span></div>
          {doc.amount_paid !== undefined && <Row label="Paid" value={doc.amount_paid} />}
          {doc.balance_due !== undefined && <Row label="Balance Due" value={doc.balance_due} />}
        </div>
      </div>

      {(text.notes || text.terms || showSignature) && <div className="document-print-footer grid grid-cols-2 gap-4 mt-6 pt-3 border-t border-slate-300 text-[10px]">
        <div>
          {text.notes && <><div className="font-semibold mb-1">Notes</div><div className="text-slate-600 whitespace-pre-wrap">{text.notes}</div></>}
          {text.terms && <><div className="font-semibold mt-2 mb-1">Terms &amp; Conditions</div><div className="text-slate-600 whitespace-pre-wrap">{text.terms}</div></>}
        </div>
        {showSignature && <div className="document-print-signature text-right flex flex-col items-end min-w-0 max-w-full print-keep">
          <div className="font-semibold">For {c.name}</div>
          {signature && <img src={signature} alt="Authorised signature"
            style={{ maxWidth: "120px", maxHeight: "42px", objectFit: "contain", marginTop: "4px" }} />}
          {(c.signature_name || c.signature_title) && <div className="max-w-full break-words mt-1">
            {c.signature_name && <div className="font-semibold">{c.signature_name}</div>}
            {c.signature_title && <div className="text-slate-600">{c.signature_title}</div>}
          </div>}
          <div className={`border-t border-slate-400 pt-1 max-w-full ${signature || c.signature_name || c.signature_title ? "mt-2" : "mt-8"}`}>
            Authorised Signatory
          </div>
        </div>}
      </div>}
    </div>
  );
}

function BankFields({ bank }) {
  const fields = [
    (bank.bank_name || bank.name) && `Bank: ${bank.bank_name || bank.name}`,
    (bank.bank_account || bank.account_number || bank.account) && `A/C: ${bank.bank_account || bank.account_number || bank.account}`,
    (bank.bank_ifsc || bank.ifsc) && `IFSC: ${bank.bank_ifsc || bank.ifsc}`,
    bank.branch_name && `Branch: ${bank.branch_name}`,
    bank.swift_code && `SWIFT: ${bank.swift_code}`,
    bank.upi_id && `UPI: ${bank.upi_id}`,
  ].filter(Boolean);
  return <>{fields.map((field) => <div key={field}>{field}</div>)}</>;
}

function Th({ children, right }) {
  return <th className={`px-1.5 py-1 border border-slate-300 ${right ? "text-right" : "text-left"}`}>{children}</th>;
}

function Td({ children, right, mono }) {
  return <td className={`px-1.5 py-1 border border-slate-200 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""}`}>{children}</td>;
}

function Row({ label, value }) {
  return <div className="flex justify-between py-0.5"><span className="text-slate-600">{label}</span><span className="font-mono">{Number(value || 0).toFixed(2)}</span></div>;
}